// Estado de "pensando" del chatbot de nutrición.
//
// Entre el envío y el primer texto el agente puede pasar varios segundos
// consultando el perfil y los registros del atleta, sin emitir nada visible.
// Para que el chat no parezca congelado mostramos una secuencia de pasos: avanza
// con el reloj, pero salta adelante en cuanto llegan las llamadas reales a
// herramientas del agente (partes `tool-*` del stream), así nunca va por detrás
// de lo que el servidor ya hizo.

export const THINKING_STEPS = [
  { id: "read", label: "Entendiendo tu pregunta" },
  { id: "profile", label: "Revisando tu perfil deportivo", tool: "leer_perfil_atleta" },
  { id: "tracking", label: "Mirando tus registros recientes", tool: "leer_registro_reciente" },
  { id: "plan", label: "Calculando calorías y macros" },
  { id: "write", label: "Preparando tu respuesta" },
];

// Momento (ms desde que empezó a pensar) en que cada paso se activa por reloj.
export const STEP_SCHEDULE_MS = [0, 1400, 3400, 5800, 8400];

export const THINKING_PHRASES = [
  "Contando gramos de proteína…",
  "Pesando la avena del desayuno…",
  "Midiendo tu gasto de energía…",
  "Equilibrando carbohidratos y grasas…",
  "Pensando en tu recuperación muscular…",
  "Ajustando las porciones a tu deporte…",
  "Comparando opciones pre-entreno…",
  "Picando verduras mentalmente…",
  "Revisando la hidratación…",
  "Afinando los últimos detalles…",
];

export const PHRASE_INTERVAL_MS = 2600;

// A partir de aquí admitimos que tarda más de lo normal, en vez de fingir.
export const SLOW_THRESHOLD_MS = 15000;

const DONE_TOOL_STATES = new Set(["output-available", "output-error", "output-denied"]);

/**
 * Llamadas a herramientas de un UIMessage del asistente.
 * Cubre tanto `tool-<nombre>` (herramientas estáticas) como `dynamic-tool`.
 * @returns {{ name: string, done: boolean }[]}
 */
export const toolCalls = (message) =>
  (message?.parts ?? []).flatMap((part) => {
    if (typeof part?.type !== "string") return [];
    let name = null;
    if (part.type === "dynamic-tool") name = part.toolName;
    else if (part.type.startsWith("tool-")) name = part.type.slice("tool-".length);
    return name ? [{ name, done: DONE_TOOL_STATES.has(part.state) }] : [];
  });

/**
 * Índice del paso en curso: el más avanzado entre el reloj y lo que el agente
 * realmente hizo. Nunca pasa del último paso — ése sólo termina cuando llega
 * el texto y el indicador desaparece.
 */
export const activeStepIndex = (elapsedMs, calls = []) => {
  const last = THINKING_STEPS.length - 1;

  let byClock = 0;
  STEP_SCHEDULE_MS.forEach((at, i) => {
    if (elapsedMs >= at) byClock = i;
  });

  let byTools = 0;
  THINKING_STEPS.forEach((step, i) => {
    if (!step.tool) return;
    const matching = calls.filter((c) => c.name === step.tool);
    if (matching.length === 0) return;
    const reached = matching.every((c) => c.done) ? i + 1 : i;
    byTools = Math.max(byTools, reached);
  });

  return Math.min(Math.max(byClock, byTools), last);
};

/**
 * Cierra el markdown que el stream dejó abierto a mitad de token, para que un
 * `**negrita` a medio llegar no parpadee como asteriscos literales.
 */
export const closeOpenMarkdown = (text) => {
  if (!text) return text;
  let out = text;
  const fences = (out.match(/```/g) ?? []).length;
  if (fences % 2 === 1) return `${out}\n\`\`\``;
  const withoutFences = out.replace(/```[\s\S]*?```/g, "");
  if ((withoutFences.match(/`/g) ?? []).length % 2 === 1) out += "`";
  if ((withoutFences.match(/\*\*/g) ?? []).length % 2 === 1) out += "**";
  return out;
};
